import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../api.js';
import { supabase } from '../../lib/supabase';
import { fixImageUrl } from '../../utils/fixImageUrl';
import './HomeSidebar.css';

function useAutoTick(count, ms = 4500) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (count < 2) return;
    const t = setInterval(() => setIdx(i => (i + 1) % count), ms);
    return () => clearInterval(t);
  }, [count, ms]);
  return [idx, setIdx];
}

function SideCard({ title, items, renderItem, href, hrefLabel, slideH }) {
  const [idx, setIdx] = useAutoTick(items.length);
  if (!items.length) return null;
  return (
    <div className="hside-card">
      <div className="hside-head">
        <span className="hside-label">{title}</span>
        {href && <a href={href} className="hside-all">{hrefLabel}</a>}
      </div>
      <div className="hside-vp" style={{ height: slideH }}>
        <div className="hside-track" style={{ transform: `translateY(-${idx * slideH}px)` }}>
          {items.map((item, i) => (
            <div key={i} className="hside-slide" style={{ height: slideH }}>
              {renderItem(item)}
            </div>
          ))}
        </div>
      </div>
      <div className="hside-bar">
        <div key={idx} className="hside-bar-fill" />
      </div>
    </div>
  );
}

function ArtworkCard({ slides, isAr }) {
  const [idx, setIdx] = useAutoTick(slides.length, 5000);
  if (!slides.length) return null;
  return (
    <div className="hside-card hside-card-artwork">
      <div className="hside-head">
        <span className="hside-label">{isAr ? 'أعمال الفنانين' : "Artists' Works"}</span>
        <a href="/ArtistsList" className="hside-all">{isAr ? 'الكل' : 'All'}</a>
      </div>
      <div className="hside-artwork-vp">
        <div className="hside-artwork-track" style={{ transform: `translateX(-${idx * 100}%)` }}>
          {slides.map((s, i) => (
            <a key={i} href={`/ArtistProfile/${s.artist.id}`} className="hside-artwork-slide">
              <img
                src={fixImageUrl(s.imageUrl)} alt=""
                className="hside-artwork-img"
                onError={e => { e.target.style.display = 'none'; }}
              />
              <div className="hside-artwork-info">
                <div className="hside-artwork-name">
                  {isAr ? s.artist.name_ar || s.artist.name : s.artist.name}
                </div>
                {(s.artist.specialty || s.artist.specialty_ar) && (
                  <div className="hside-artwork-spec">
                    {isAr ? s.artist.specialty_ar || s.artist.specialty : s.artist.specialty || s.artist.specialty_ar}
                  </div>
                )}
                <div className="hside-artwork-round">
                  {isAr ? `عمل ${s.imgIdx + 1}` : `Work ${s.imgIdx + 1}`}
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
      <div className="hside-bar"><div key={idx} className="hside-bar-fill" /></div>
    </div>
  );
}

export default function HomeSidebar({ news = [], isAr }) {
  const [artworkSlides, setArtworkSlides] = useState([]);
  const [threads,       setThreads]       = useState([]);
  const [books,         setBooks]         = useState([]);
  const [alrakoba,      setAlrakoba]      = useState([]);

  useEffect(() => {
    // Artworks: spiral through all approved artists' image arrays
    supabase
      .from('artists')
      .select('id, name, name_ar, specialty, specialty_ar, images')
      .eq('status', 'approved')
      .eq('is_visible', true)
      .order('sort_order', { nullsFirst: false })
      .order('name')
      .then(({ data }) => {
        const artists = (data || []).filter(a => a.images?.length);
        if (!artists.length) return;
        const maxImgs = Math.max(...artists.map(a => a.images.length));
        const slides = [];
        for (let imgIdx = 0; imgIdx < maxImgs; imgIdx++) {
          for (const artist of artists) {
            if (artist.images[imgIdx]) slides.push({ artist, imageUrl: artist.images[imgIdx], imgIdx });
          }
        }
        setArtworkSlides(slides);
      })
      .catch(() => {});

    fetch(`${API_BASE}/api/forum/threads?limit=10`)
      .then(r => r.json())
      .then(d => setThreads(d.threads || []))
      .catch(() => {});
    fetch(`${API_BASE}/api/forum/bookshelf?collection=bookshelf`)
      .then(r => r.json())
      .then(d => setBooks(Array.isArray(d) ? d.slice(0, 10) : []))
      .catch(() => {});
    fetch(`${API_BASE}/api/news/alrakoba`)
      .then(r => r.json())
      .then(d => setAlrakoba(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const ar = isAr;

  return (
    <aside className="home-sidebar">

      {/* ── Artists' Artworks (horizontal, spiral order) ── */}
      <ArtworkCard slides={artworkSlides} isAr={isAr} />

      {/* ── News ── */}
      <SideCard
        title={ar ? 'آخر الأخبار' : 'Latest News'}
        href="/Events" hrefLabel={ar ? 'الكل' : 'All'}
        items={news.slice(0, 8)}
        slideH={196}
        renderItem={item => (
          <a href={item.link} target="_blank" rel="noopener noreferrer" className="hside-news">
            {item.image_url && (
              <img src={item.image_url} alt="" className="hside-news-img"
                onError={e => { e.target.style.display = 'none'; }} />
            )}
            <div className="hside-news-body">
              <div className="hside-news-src">{item.source_id}</div>
              <div className="hside-news-ttl">
                {item.title?.length > 90 ? item.title.slice(0, 90) + '…' : item.title}
              </div>
              <div className="hside-news-date">{item.pubDate?.slice(0, 10)}</div>
            </div>
          </a>
        )}
      />

      {/* ── Threads ── */}
      <SideCard
        title={ar ? 'آخر النقاشات' : 'Latest Threads'}
        href="/Forum" hrefLabel={ar ? 'الكل' : 'All'}
        items={threads}
        slideH={118}
        renderItem={item => (
          <a href="/Forum" className="hside-thread">
            <div className="hside-thread-ttl">
              {(ar ? item.title_ar : item.title_en) || item.title_en || item.title_ar}
            </div>
            <div className="hside-thread-meta">
              <span>💬 {item.replyCount || 0}</span>
              <span>{item.createdAt?.slice(0, 10)}</span>
            </div>
          </a>
        )}
      />

      {/* ── Books ── */}
      <SideCard
        title={ar ? 'آخر إضافات الرف' : 'Latest Books'}
        href="/Forum" hrefLabel={ar ? 'الكل' : 'All'}
        items={books}
        slideH={140}
        renderItem={item => {
          const dest  = item.link_url || item.fileUrl;
          const Wrap  = dest ? 'a' : 'div';
          const wrapP = dest ? { href: dest, target: '_blank', rel: 'noopener noreferrer' } : {};
          const ttl   = (ar ? item.title_ar : item.title_en) || item.title_en || item.title_ar;
          const auth  = (ar ? item.author_ar : item.author_en) || item.author_en || item.author_ar;
          return (
            <Wrap className="hside-book" {...wrapP}>
              {item.coverUrl
                ? <img src={item.coverUrl} alt="" className="hside-book-cov"
                    onError={e => { e.target.style.display = 'none'; }} />
                : <div className="hside-book-cov hside-book-ph">📚</div>
              }
              <div className="hside-book-body">
                <div className="hside-book-ttl">{ttl}</div>
                {auth && <div className="hside-book-auth">{auth}</div>}
                <div className="hside-book-type">{item.type}</div>
              </div>
            </Wrap>
          );
        }}
      />

      {/* ── Alrakoba ── */}
      <SideCard
        title={ar ? 'الراكوبة — أخبار' : 'Alrakoba News'}
        href="https://www.alrakoba.net" hrefLabel={ar ? 'الموقع' : 'Site'}
        items={alrakoba}
        slideH={196}
        renderItem={item => (
          <a href={item.link} target="_blank" rel="noopener noreferrer" className="hside-news">
            {item.image && (
              <img src={item.image} alt="" className="hside-news-img"
                onError={e => { e.target.style.display = 'none'; }} />
            )}
            <div className="hside-news-body">
              <div className="hside-news-src" style={{ color: 'var(--green, #1e8a4a)' }}>الراكوبة</div>
              <div className="hside-news-ttl">{item.title?.length > 90 ? item.title.slice(0, 90) + '…' : item.title}</div>
              <div className="hside-news-date">{item.pubDate?.slice(0, 16)}</div>
            </div>
          </a>
        )}
      />

    </aside>
  );
}
